import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { FadeIn, useFadeInOnScroll } from "@/lib/hooks";
import { CATEGORY_DETAILS, IMG, BRAND } from "@/lib/data";
import { COLLECTIONS } from "@/constants/testIds";

export default function Collections() {
    useFadeInOnScroll();
    const location = useLocation();
    const navigate = useNavigate();

    const goToConsultation = (category) => {
        navigate({
            pathname: "/",
            search: category ? `?category=${encodeURIComponent(category)}` : "",
            hash: "consultation",
        });
    };

    // Scroll to category if hash present
    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
            }
        }
    }, [location.hash]);

    return (
        <main data-testid={COLLECTIONS.root} className="bg-[#0A0A0A] text-[#F1F1EE]">
            {/* HERO */}
            <section data-testid={COLLECTIONS.hero} className="relative h-[80vh] min-h-[540px] overflow-hidden">
                <img src={IMG.heroCollections} alt="Curated collections" className="absolute inset-0 w-full h-full object-cover slow-zoom" />
                <div className="absolute inset-0 bg-black/55" />
                <div className="relative z-10 h-full flex flex-col justify-end pb-24 px-6 md:px-16 max-w-[1600px] mx-auto text-white">
                    <FadeIn className="visible">
                        <p className="overline text-white/80">The Emporium</p>
                    </FadeIn>
                    <FadeIn delay={140} className="visible mt-6 max-w-4xl">
                        <h1 className="font-serif-display text-5xl sm:text-6xl md:text-8xl leading-[0.95]">
                            Curated <span className="italic">Collections</span>.
                        </h1>
                    </FadeIn>
                    <FadeIn delay={280} className="visible mt-6 max-w-2xl">
                        <p className="text-base md:text-lg text-white/80 font-light leading-relaxed">
                            Explore handcrafted treasures collected over four decades — from museum-quality silk carpets to rare temple bronzes.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* CATEGORY INDEX BAR */}
            <div className="sticky top-20 md:top-24 z-30 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1F1F1F]">
                <div className="max-w-[1600px] mx-auto px-4 md:px-8">
                    <div className="flex items-center gap-6 md:gap-10 overflow-x-auto no-scrollbar py-4">
                        {CATEGORY_DETAILS.map((c) => (
                            <a
                                key={c.slug}
                                href={`#${c.slug}`}
                                className="overline whitespace-nowrap text-[#F1F1EE] gold-underline flex-shrink-0"
                            >
                                {c.title}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            {/* CATEGORIES */}
            {CATEGORY_DETAILS.map((cat, idx) => {
                const reverse = idx % 2 === 1;
                return (
                    <section
                        key={cat.slug}
                        id={cat.slug}
                        data-testid={COLLECTIONS.category(cat.slug)}
                        className={`px-6 md:px-12 py-24 md:py-32 border-b border-[#1A1A1A] ${idx % 2 === 0 ? "bg-[#0A0A0A]" : "bg-[#111111]"} scroll-mt-32`}
                    >
                        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                            <FadeIn className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                                <div className="img-hover-wrap aspect-[4/5]">
                                    <img src={cat.image} alt={cat.title} loading="lazy" className="w-full h-full object-cover" />
                                </div>
                            </FadeIn>
                            <FadeIn delay={120} className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                                <p className="overline text-[#D4AF37]">Collection · 0{idx + 1}</p>
                                <h2 className="font-serif-display text-4xl md:text-6xl mt-6 text-[#F1F1EE] leading-[1.02]">
                                    {cat.title}
                                </h2>
                                <div className="hairline-gold mt-8" />
                                <p className="mt-8 text-[#B8B8B8] font-light text-base md:text-lg leading-relaxed max-w-xl">
                                    {cat.description}
                                </p>
                                <ul className="mt-10 flex flex-wrap gap-x-3 gap-y-3">
                                    {cat.items.map((it) => (
                                        <li
                                            key={it}
                                            className="border border-[#2A2A2A] bg-[#0A0A0A] px-4 py-2 text-xs uppercase tracking-widest text-[#F1F1EE] font-medium"
                                        >
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    type="button"
                                    onClick={() => goToConsultation(cat.option)}
                                    data-testid={COLLECTIONS.enquireBtn(cat.slug)}
                                    className="btn-outline-dark group mt-10"
                                >
                                    Enquire About {cat.title}
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.6} />
                                </button>
                            </FadeIn>
                        </div>
                    </section>
                );
            })}

            {/* PRIVATE COLLECTION CTA */}
            <section data-testid={COLLECTIONS.privateCta} className="premium-dark-bg text-white px-6 md:px-12 py-24 md:py-40 relative overflow-hidden">
                <div className="relative z-10 max-w-3xl mx-auto text-center">
                    <p className="overline text-[#D4AF37]">Private Sourcing</p>
                    <h2 className="font-serif-display text-4xl md:text-6xl mt-6 leading-[1.02]">
                        Looking for a specific piece?
                    </h2>
                    <p className="mt-8 text-white/80 font-light text-base md:text-lg max-w-xl mx-auto">
                        Our team can help source exclusive handcrafted collections based on your preferences. Share what you have in mind — we&rsquo;ll bring the possibilities to you.
                    </p>
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => goToConsultation()}
                            data-testid={COLLECTIONS.privateCtaBtn}
                            className="btn-primary group"
                        >
                            <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
                            Send Private Enquiry
                        </button>
                    </div>
                    <p className="mt-8 text-xs text-white/60 tracking-wider">
                        Prefer to chat directly? WhatsApp&nbsp;
                        <a
                            href={`https://wa.me/${BRAND.phoneRaw}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-[#D4AF37]"
                        >
                            {BRAND.phone}
                        </a>
                    </p>
                </div>
            </section>
        </main>
    );
}
